import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardFooter, Row, Col } from 'reactstrap';
import { Field, FormAlerts, Button, PseudoStates } from '@cordata/buffet';
import { withFormik } from 'formik';
import { forEach, startCase, lowerCase } from 'lodash';
import * as Yup from 'yup';

import {
  adminInstance as axios,
  toastSuccess,
} from '~/utils';

import useSlice from '~/utils/slice/hook';

const initialValues = {
  org: null,
  id: undefined,
  options: [],
  passwordMinLength: 8,
  maxBadPassword: 5,
  sendAccountLockedEmail: true,
  sendBadPasswordEmail: true,
  sendBadPasswordEmailIndex: 2,
  passwordValidDays: 90,
  passwordReuseCount: 5,
};

const passwordBooleanFields = [
  'requires uppercase',
  'requires lowercase',
  'requires special',
  'requires number',
];

const formikHoc = withFormik({
  displayName: 'PasswordPolicyFormik',
  validateOnChange: false,
  validateOnBlur: false,

  mapPropsToValues: () => initialValues,

  validationSchema: () => Yup.object().shape({
    maxBadPassword: Yup.mixed().required(),
  }),

  handleSubmit: (v, { resetForm, setSubmitting, setErrors }) => {
    const p = {
      ...v,
      ...passwordBooleanFields.reduce((a, m) => ({
        ...a,
        [`password${startCase(m).replace(/ /g, '')}`]: v.options.some(o => o.includes(m)),
      }), {}),
      orgUid: v.org.id,
    };

    const method = v.id ? 'put' : 'post';
    const url = `/organization-security-settings${v.id ? `/${v.id}` : ''}`;

    delete p.org;
    delete p.options;

    axios[method](url, p)
      .then(() => {
        toastSuccess();
        resetForm(initialValues);
      })
      .catch(setErrors)
      .then(() => setSubmitting(false));
  },
});

const PasswordPolicyForm = ({
  values,
  errors,
  setFieldValue,
  handleSubmit,
  submitCount,
  isSubmitting,
}) => {
  const { fetch } = useSlice();
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [working, setWorking] = useState(true);
  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    fetch('organization')
      .then(r => r.data.data)
      .then(setOrgs)
      .catch(setError)
      .then(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    setWorking(true);

    if (values.org) {
      axios
        .get(`/organization-security-settings?org-uid=${values.org.id}`)
        .then(r => r.data.data[0])
        .then((r) => {
          const options = [];

          if (r) {
            forEach(r.attributes, (v, k) => {
              setFieldValue(k, typeof v === 'number' || v ? v : initialValues[k]);

              if (k.includes('passwordRequires') && v) {
                options.push(lowerCase(k.slice(8)));
              }
            });

            setFieldValue('options', options);
            setFieldValue('id', r.id);
          } else {
            forEach(initialValues, (v, k) => {
              if (k !== 'org') {
                setFieldValue(k, v);
              }
            });
          }

          setWorking(false);
        });
    }
  }, [values.org]);

  return !isLoading && !error ? (
    <Card>
      <form onSubmit={handleSubmit} noValidate>
        <CardBody className="p-0">
          <fieldset>
            { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }
            <Field
              name="org"
              label="Organization"
              type="select"
              options={orgs}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes.orgName}
            />
          </fieldset>

          {!working && (
            <fieldset>
              <Row form>
                <Col sm>
                  <Field
                    name="options"
                    label="Policies"
                    type="checkbox-group"
                    options={passwordBooleanFields}
                    loading={working}
                    disabled={!values.org}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm>
                  <Field
                    name="passwordMinLength"
                    label="Minimum password length"
                    type="number"
                    loading={working}
                    disabled={!values.org}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="maxBadPassword"
                    label="Maximum bad password attempts"
                    type="number"
                    loading={working}
                    disabled={!values.org}
                    required
                  />
                </Col>

                <Col sm>
                  <Field
                    name="passwordValidDays"
                    label="Days password is valid"
                    type="number"
                    loading={working}
                    disabled={!values.org}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="passwordReuseCount"
                    type="number"
                    loading={working}
                    disabled={!values.org}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm>
                  <Field
                    name="sendBadPasswordEmail"
                    type="checkbox"
                    loading={working}
                    disabled={!values.org}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="sendAccountLockedEmail"
                    type="checkbox"
                    loading={working}
                    disabled={!values.org}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="sendBadPasswordEmailIndex"
                    type="number"
                    loading={working}
                    disabled={!values.org}
                  />
                </Col>
              </Row>
            </fieldset>
          )}
        </CardBody>

        <CardFooter className="d-flex">
          <div className="ml-auto">
            <Button type="submit" disabled={isSubmitting || !values.org}>
              {isSubmitting ? 'Working ...' : 'Save'}
            </Button>
          </div>
        </CardFooter>
      </form>
    </Card>
  ) : (
    <PseudoStates
      isLoading={isLoading}
      error={error}
    />
  );
};

export default formikHoc(PasswordPolicyForm);
